import { useState } from 'react';
import { Button } from '@/components/base/Button';
import { useDialogs } from '@/components/layouts/dialogs/DialogProvider';
import { t } from '@/messages/zh-TW';
import { useResetTestData } from '@/mocks';

/**
 * 第二層版型：「重置測試資料」鈕（batch 0）。
 *
 * **原系統沒有對應**——這不是 legacy navbar.jsp 的一部分，是雛形本身的展示／測試工具：
 * `MockDataSource` 從 batch 0 起把 CRUD 異動寫進 localStorage（見
 * `src/mocks/mock-data-source.ts`），重新整理頁面不再回到種子資料，所以需要一個明確的
 * 入口把它清回去。放在 `AppLayout.tsx` 組裝 `<Navbar devToolbar>` slot，跟 `<RoleSwitcher>`
 * 一樣掛在操作區——不塞進 `roleSwitcher` slot，因為那個 slot 的語意是「身分切換區」
 * （見 `Navbar.tsx` 的 `roleSwitcher` prop JSDoc），跟這顆鈕的用途無關。
 *
 * 破壞性操作，比照 `CrudListPage.tsx` 的刪除鈕先跳 `useDialogs().confirm()`。
 */
export interface ResetDataButtonProps {
  uiId: string;
}

export function ResetDataButton({ uiId }: ResetDataButtonProps) {
  const dialogs = useDialogs();
  const resetTestData = useResetTestData();
  // reload 通常會在下一個 tick 就把整頁換掉，這裡只是防止 confirm 對話框關閉、
  // reload 真的生效之間的空檔被連點兩次。
  const [pending, setPending] = useState(false);

  async function handleClick(): Promise<void> {
    const confirmed = await dialogs.confirm(t('confirm.resetTestData'));
    if (!confirmed) {
      return;
    }
    setPending(true);
    resetTestData();
  }

  return (
    <Button uiId={uiId} variant="default" size="sm" disabled={pending} onClick={handleClick}>
      {t('action.resetTestData')}
    </Button>
  );
}
